import React, { useState, useEffect, useMemo } from 'react';
import { Nav, Button } from 'react-bootstrap';
import { FaPhone, FaEnvelope, FaStickyNote, FaCalendarAlt, FaPlus, FaChevronDown, FaChevronRight, FaPen } from 'react-icons/fa';
import { formatDate, formatDateTime } from '../../utils/formatUtils';
import { theme } from '../../theme/theme.js';
import DataApi from '../../api/DataApi';
import EditLayoutModal from './EditLayoutModal';
import './css/TaskActivityTimeline.css';

// Helper functions for activity icons and colors
const getActivityIcon = (type) => {
  switch (type.toLowerCase()) {
    case 'call':
      return FaPhone;
    case 'meeting':
      return FaCalendarAlt;
    case 'note':
      return FaStickyNote;
    case 'email':
      return FaEnvelope;
    default:
      return FaStickyNote;
  }
};

const getActivityColor = (type) => {
  switch (type.toLowerCase()) {
    case 'call':
      return 'blue';
    case 'meeting':
      return 'purple';
    case 'note':
      return 'grey';
    case 'email':
      return 'green';
    default:
      return 'grey';
  }
};

const ACTIVITY_DESCRIPTION_MAX_LENGTH = 100;

const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/** Plain preview for timeline; notes are measured after stripping HTML. */
const getTruncatedDescriptionText = (description, parentObject) => {
  if (!description || typeof description !== 'string') return '';
  const plain =
    parentObject === 'note'
      ? stripHtmlTags(description)
      : description.trim().replace(/\s+/g, ' ');
  if (plain.length <= ACTIVITY_DESCRIPTION_MAX_LENGTH) return plain;
  return `${plain.slice(0, ACTIVITY_DESCRIPTION_MAX_LENGTH)}...`;
};

const formatTaskStatusLabel = (status) => {
  if (status === undefined || status === null || status === '') return '';
  return String(status)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const normalizeFilterKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');

const isNoteActivity = (activity) => {
  const activityType = (activity.activityType || '').toLowerCase();
  const parentObject = (activity.parentObject || '').toLowerCase();
  return (
    activityType === 'note' ||
    activityType === 'notes' ||
    parentObject === 'note' ||
    (activity.record &&
      (activity.record.parent_object === 'Note' || activity.record.activity_type === 'Note'))
  );
};

const isEmailActivity = (activity) => {
  const activityType = (activity.activityType || '').toLowerCase();
  const parentObject = (activity.parentObject || '').toLowerCase();
  return activityType === 'email' || parentObject === 'email_log';
};

/** Build filter tabs from activities: All + task types present + Notes/Emails when present. */
const buildFilterTabs = (activities) => {
  const tabs = [{ key: 'all', label: 'All' }];
  const taskTypeByKey = new Map();
  let hasNotes = false;
  let hasEmails = false;

  activities.forEach((activity) => {
    if (isNoteActivity(activity)) {
      hasNotes = true;
      return;
    }
    if (isEmailActivity(activity)) {
      hasEmails = true;
      return;
    }
    if (activity.parentObject === 'task' && activity.taskTypeLabel) {
      const label = String(activity.taskTypeLabel).trim();
      if (!label) return;
      const key = normalizeFilterKey(label);
      if (!taskTypeByKey.has(key)) {
        taskTypeByKey.set(key, label);
      }
    }
  });

  [...taskTypeByKey.entries()]
    .sort(([, labelA], [, labelB]) => labelA.localeCompare(labelB))
    .forEach(([key, label]) => {
      tabs.push({ key, label });
    });

  if (hasNotes) tabs.push({ key: 'notes', label: 'Notes' });
  if (hasEmails) tabs.push({ key: 'emails', label: 'Emails' });

  return tabs;
};

const activityMatchesFilterTab = (activity, tabKey) => {
  if (tabKey === 'all') return true;
  if (tabKey === 'notes') return isNoteActivity(activity);
  if (tabKey === 'emails') return isEmailActivity(activity);
  if (activity.parentObject === 'task') {
    const typeKey = normalizeFilterKey(activity.taskTypeLabel || activity.activityType);
    return typeKey === tabKey;
  }
  return false;
};

/** Task rows from ActivityService: meetings use activity_date=start; others use due (date). */
const getActivityScheduleDisplay = (activity) => {
  if (!activity?.date) return null;

  const at = (activity.activityType || '').toLowerCase();
  const isMeetingTask =
    activity.parentObject === 'task' && (at === 'meeting' || at === 'meetings');

  const isOtherTask = activity.parentObject === 'task' && !isMeetingTask;

  if (isMeetingTask) {
    return { label: 'Meeting starts', text: formatDateTime(activity.date) };
  }
  if (isOtherTask) {
    return { label: 'Due', text: formatDate(activity.date) };
  }
  return { label: null, text: formatDateTime(activity.date) };
};

// Transform related list records format to activity format
const transformRecordsToActivities = (records) => {
    if (!records || !Array.isArray(records)) {
      console.log('transformRecordsToActivities - Invalid records:', records);
      return [];
    }

    console.log('transformRecordsToActivities - Processing', records.length, 'records');

    return records.map((record, index) => {
      console.log(`transformRecordsToActivities - Record ${index}:`, record);

      // Convert either:
      // - related list shape: { id, fields: [{fieldName, value, displayValue, ...}] }
      // - fetchRecords shape: { fieldName: {value, displayValue, referenceObject, ...} | primitive }
      const activity = {};
      if (record && typeof record === 'object') {
        // Record id can be string or {value,...}
        if (record.id && typeof record.id === 'object' && 'value' in record.id) {
          activity.id = record.id.value;
        } else {
          activity.id = record.id;
        }
      }

      if (record?.fields && Array.isArray(record.fields)) {
        console.log(
          `transformRecordsToActivities - Record ${index} has ${record.fields.length} fields`
        );
        record.fields.forEach(field => {
          if (!field?.fieldName) return;
          // Use displayValue if available (for lookup fields)
          activity[field.fieldName] =
            field.displayValue !== undefined && field.displayValue !== null
              ? field.displayValue
              : field.value;
        });
      } else if (record && typeof record === 'object') {
        // fetchRecords row shape
        Object.keys(record).forEach(key => {
          if (key === 'id') return;
          const v = record[key];
          if (v && typeof v === 'object') {
            // Prefer displayValue if present, else value, else fall back
            if ('displayValue' in v && v.displayValue !== null && v.displayValue !== undefined) {
              activity[key] = v.displayValue;
            } else if ('value' in v) {
              activity[key] = v.value;
            } else {
              activity[key] = v;
            }
          } else {
            activity[key] = v;
          }
        });
      } else {
        console.warn(
          `transformRecordsToActivities - Record ${index} is not an object:`,
          record
        );
      }

      console.log(`transformRecordsToActivities - Record ${index} transformed activity:`, activity);

      // Helper function to extract value from field (handles both string and object formats)
      const extractValue = (field) => {
        if (!field) return '';
        if (typeof field === 'object' && field !== null && 'value' in field) {
          return field.value || '';
        }
        return String(field || '');
      };

      // Get activity_type and parent_object, handling both lowercase and original case
      // Handle both string values and object formats (with 'value' property)
      const rawActivityType = extractValue(activity.activity_type);
      const rawParentObject = extractValue(activity.parent_object);
      const activityType = rawActivityType.toLowerCase();
      const parentObject = rawParentObject.toLowerCase();

      // Determine icon and color based on parent_object and activity_type
      let iconType = activityType;
      if (parentObject === 'email_log') {
        iconType = 'email';
      } else if (parentObject === 'note' || rawParentObject === 'Note') {
        // Notes: parent_object is 'Note' (capital) or 'note' (lowercase)
        iconType = 'note';
      } else if (parentObject === 'task') {
        iconType = activityType || 'note';
      }

      // Ensure notes are properly identified even if activity_type is not set
      // Check both lowercase and original case
      if ((parentObject === 'note' || rawParentObject === 'Note' || rawActivityType === 'Note') && !iconType) {
        iconType = 'note';
      }

      // Extract field values that might be objects
      const activityName = extractValue(activity.name);
      const status = extractValue(activity.status);
      const parentStatus = extractValue(activity.parent_status);
      const parentId = extractValue(activity.parent_id);
      const description = extractValue(activity.description);
      const activityDate = extractValue(activity.activity_date);
      const createdDate = extractValue(activity.createddate);

      // Determine title based on activity type and parent object
      let title = activityName || '';
      if (!title || title === 'Untitled') {
        if (parentObject === 'email_log') {
          title = activityName || 'Email';
        } else if (parentObject === 'note') {
          title = activityName || 'Note';
        } else if (parentObject === 'task') {
          title = activityName || 'Task';
        } else {
          title = activityName || 'Activity';
        }
      }

      return {
        id: activity.id,
        type: iconType,
        activityType: iconType,
        taskTypeLabel: parentObject === 'task' ? (rawActivityType || '') : '',
        parentObject: parentObject,
        parentId: parentId,
        title: title || activityName,
        description: description,
        date: activityDate || createdDate,
        status: status,
        taskStatus: parentObject === 'task' && parentStatus ? parentStatus : null,
        icon: getActivityIcon(iconType),
        color: getActivityColor(iconType),
        record: activity
      };
    });
};

const extractRecordFieldValue = (record, fieldName) => {
  if (!record) return '';
  if (Array.isArray(record.fields)) {
    const field = record.fields.find((f) => f.fieldName === fieldName);
    if (!field) return '';
    return field.displayValue ?? field.value ?? '';
  }
  const value = record[fieldName];
  if (value && typeof value === 'object' && value !== null) {
    return value.displayValue ?? value.value ?? '';
  }
  return value ?? '';
};

const fetchTaskStatusesById = async (taskIds) => {
  const uniqueIds = [...new Set(taskIds.filter(Boolean))];
  if (uniqueIds.length === 0) return new Map();

  const queryParams = new URLSearchParams({
    fields: 'id,status',
    limit: String(uniqueIds.length),
    filter0: `id:in:${uniqueIds.join(';')}`,
  }).toString();

  const response = await new DataApi('object/list/task').fetchAll(queryParams);
  const rows = response?.data?.data;
  if (!Array.isArray(rows)) return new Map();

  const statusMap = new Map();
  rows.forEach((row) => {
    const id = extractRecordFieldValue(row, 'id');
    const status = extractRecordFieldValue(row, 'status');
    if (id) statusMap.set(String(id), status);
  });
  return statusMap;
};

/**
 * TaskActivityTimeline Component
 * Displays a timeline of activities (tasks, emails, notes) related to a record
 * Records must come from related list query - no duplicate API calls
 * @param {string} recordId - The record ID (for Log Activity button)
 * @param {string} objectName - The object name (for Log Activity button)
 * @param {Function} onRefresh - Callback when refresh is needed
 * @param {boolean} hideLogActivityButton - Whether to hide the log activity button
 * @param {Array} records - Pre-fetched records from related list query (required)
 */
const TaskActivityTimeline = ({ recordId, objectName, onRefresh, hideLogActivityButton = false, records = [] }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [editingTaskId, setEditingTaskId] = useState(null);
  /** Keys are date group labels (e.g. Today, Past); true = section body collapsed */
  const [collapsedSections, setCollapsedSections] = useState({});

  const toggleSection = (dateLabel) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [dateLabel]: !prev[dateLabel]
    }));
  };

  useEffect(() => {
    let cancelled = false;

    const loadActivities = async () => {
      setLoading(true);
      let transformedActivities = transformRecordsToActivities(records || []);

      const taskIdsMissingStatus = transformedActivities
        .filter((activity) => activity.parentObject === 'task' && activity.parentId && !activity.taskStatus)
        .map((activity) => activity.parentId);

      if (taskIdsMissingStatus.length > 0) {
        try {
          const statusMap = await fetchTaskStatusesById(taskIdsMissingStatus);
          if (!cancelled && statusMap.size > 0) {
            transformedActivities = transformedActivities.map((activity) => {
              if (activity.parentObject !== 'task' || !activity.parentId || activity.taskStatus) {
                return activity;
              }
              const resolvedStatus = statusMap.get(String(activity.parentId));
              return resolvedStatus
                ? { ...activity, taskStatus: resolvedStatus }
                : activity;
            });
          }
        } catch (error) {
          console.warn('TaskActivityTimeline - Could not resolve task statuses:', error);
        }
      }

      if (cancelled) return;

      transformedActivities.sort((a, b) => new Date(b.date) - new Date(a.date));
      setActivities(transformedActivities);
      setLoading(false);
    };

    loadActivities();

    return () => {
      cancelled = true;
    };
  }, [records]);

  const filterTabs = useMemo(() => buildFilterTabs(activities), [activities]);

  useEffect(() => {
    if (activeTab !== 'all' && !filterTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab('all');
    }
  }, [filterTabs, activeTab]);

  const groupActivitiesByDate = (activities) => {
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    activities.forEach(activity => {
      const activityDate = new Date(activity.date);
      activityDate.setHours(0, 0, 0, 0);

      let dateLabel;
      if (activityDate.getTime() === today.getTime()) {
        dateLabel = 'Today';
      } else if (activityDate.getTime() === yesterday.getTime()) {
        dateLabel = 'Yesterday';
      } else if (activityDate > today) {
        // Future dates - all grouped under "Upcoming"
        dateLabel = 'Upcoming';
      } else {
        // All other past dates grouped under "Past"
        dateLabel = 'Past';
      }

      if (!groups[dateLabel]) {
        groups[dateLabel] = [];
      }
      groups[dateLabel].push(activity);
    });

    // Sort activities within each group
    Object.keys(groups).forEach(dateLabel => {
      if (dateLabel === 'Upcoming') {
        // For upcoming activities, sort by date ascending (earliest first)
        groups[dateLabel].sort((a, b) => new Date(a.date) - new Date(b.date));
      } else {
        // For past activities (Today, Yesterday, Past), sort by date descending (newest first)
        groups[dateLabel].sort((a, b) => new Date(b.date) - new Date(a.date));
      }
    });

    return groups;
  };

  const filteredActivities =
    activeTab === 'all'
      ? activities
      : activities.filter((activity) => activityMatchesFilterTab(activity, activeTab));

  const groupedActivities = groupActivitiesByDate(filteredActivities);

  // Sort date groups: Upcoming first, then Today, Yesterday, then Past
  const sortedDateGroups = Object.entries(groupedActivities).sort(([dateA], [dateB]) => {
    const order = ['Upcoming', 'Today', 'Yesterday', 'Past'];
    const indexA = order.indexOf(dateA);
    const indexB = order.indexOf(dateB);

    // If both are in the order array, sort by their index
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only A is in the order array, it comes first
    if (indexA !== -1) return -1;
    // If only B is in the order array, it comes first
    if (indexB !== -1) return 1;
    // Both are not in order array (shouldn't happen), maintain current order
    return 0;
  });

  const handleLogActivity = () => {
    // Navigate to create task page with pre-filled related record
    window.location.href = `/edit/task?targetField=related_record_id&targetId=${recordId}&parentObjectName=${objectName}`;
  };

  if (loading) {
    return (
      <div className="task-activity-timeline">
        <div className="activity-timeline-loading">Loading activities...</div>
      </div>
    );
  }

  return (
    <div className="task-activity-timeline">
      <div className="activity-timeline-header">
        <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="activity-tabs">
          {filterTabs.map((tab) => (
            <Nav.Item key={tab.key}>
              <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        {!hideLogActivityButton && (
          <Button
            variant="primary"
            size="sm"
            className="log-activity-btn"
            onClick={handleLogActivity}
          >
            <FaPlus className="me-1" />
            Log Activity
          </Button>
        )}
      </div>

      <div className="activity-timeline-content">
        {sortedDateGroups.length === 0 ? (
          <div className="no-activities">
            <p>No activities found</p>
          </div>
        ) : (
          sortedDateGroups.map(([dateLabel, dateActivities]) => {
            const sectionCollapsed = !!collapsedSections[dateLabel];
            return (
            <div key={dateLabel} className="activity-date-group">
              <button
                type="button"
                className="activity-date-group-toggle"
                onClick={() => toggleSection(dateLabel)}
                aria-expanded={!sectionCollapsed}
              >
                <span className="activity-date-chevron" aria-hidden>
                  {sectionCollapsed ? <FaChevronRight /> : <FaChevronDown />}
                </span>
                <span className="activity-date-label">{dateLabel}</span>
                <span className="activity-date-count">({dateActivities.length})</span>
              </button>
              {!sectionCollapsed && (
              <div className="activity-timeline-line">
                {dateActivities.map((activity, index) => {
                  const IconComponent = activity.icon;

                  // Determine detail page URL based on activity type
                  const getDetailPageUrl = () => {
                    if (activity.parentObject === 'email_log' && activity.parentId) {
                      return `/view/email_log/${activity.parentId}`;
                    } else if (activity.parentObject === 'note' && activity.parentId) {
                      return `/view/note/${activity.parentId}`;
                    } else if (activity.parentObject === 'task' && activity.parentId) {
                      return `/view/task/${activity.parentId}`;
                    } else if (activity.id) {
                      // Fallback to activity detail page
                      return `/view/activity/${activity.id}`;
                    }
                    return null;
                  };

                  const detailUrl = getDetailPageUrl();
                  const displayStatus = activity.taskStatus || activity.status;

                  const handleActivityClick = (e) => {
                    // Don't navigate if clicking on links or action buttons
                    if (e.target.closest('a, button')) {
                      return;
                    }
                    if (detailUrl) {
                      window.open(detailUrl, '_blank');
                    }
                  };

                  return (
                    <div
                      key={activity.id || index}
                      className="activity-item"
                      onClick={handleActivityClick}
                      style={{ cursor: detailUrl ? 'pointer' : 'default' }}
                    >
                      <div className={`activity-icon activity-icon-${activity.color}`}>
                        <IconComponent />
                      </div>
                      <div className="activity-content">
                        <div className="activity-title-row">
                          <div className="activity-title">
                          {(() => {
                            const activityType = (activity.activityType || '').toLowerCase();
                            const status = String(activity.status || '').toUpperCase();
                            const taskTypeLabel = activity.taskTypeLabel || 'Task';
                            const taskTypeLower = taskTypeLabel.toLowerCase();

                            if (
                              activity.parentObject === 'task' &&
                              (taskTypeLower === 'call' || taskTypeLower === 'calls') &&
                              (status === 'CANCELLED' || status === 'MISSED')
                            ) {
                              return (
                                <>
                                  Call with {activity.title} - <span className="missed-call">Missed call</span>
                                </>
                              );
                            }
                            if (activityType === 'email' || activity.parentObject === 'email_log') {
                              // Make email subject (title) clickable to navigate to email_log detail page
                              const emailLogId = activity.parentId;
                              if (emailLogId) {
                                return (
                                  <>
                                    Email "
                                    <a
                                      href={`/view/email_log/${emailLogId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: theme?.colors?.primary || '#0176d3',
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                        cursor: 'pointer'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.textDecoration = 'underline';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.textDecoration = 'none';
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {activity.title}
                                    </a>
                                    " sent
                                  </>
                                );
                              } else {
                                return (
                                  <>
                                    Email "{activity.title}" sent
                                  </>
                                );
                              }
                            } else if (isNoteActivity(activity)) {
                              return (
                                <>
                                  Note: {activity.title}
                                </>
                              );
                            } else if (activity.parentObject === 'task') {
                              return (
                                <>
                                  {taskTypeLabel}: {activity.title}
                                </>
                              );
                            } else {
                              return activity.title;
                            }
                          })()}
                          </div>
                          {activity.parentObject === 'task' && activity.parentId && (
                            <button
                              type="button"
                              className="activity-edit-btn"
                              title="Edit task"
                              aria-label="Edit task"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTaskId(activity.parentId);
                              }}
                            >
                              <FaPen />
                            </button>
                          )}
                        </div>
                        {(() => {
                          const sched = getActivityScheduleDisplay(activity);
                          if (!sched) return null;
                          return (
                            <div className="activity-datetime">
                              {sched.label ? (
                                <>
                                  <span className="activity-datetime-label">{sched.label}</span>
                                  : {sched.text}
                                </>
                              ) : (
                                sched.text
                              )}
                            </div>
                          );
                        })()}
                        {activity.parentObject === 'task' && displayStatus && (
                          <div className="activity-task-status">
                            <span className="activity-task-status-label">Status</span>
                            <span className="activity-task-status-value">
                              {formatTaskStatusLabel(displayStatus)}
                            </span>
                          </div>
                        )}
                        {/* Hide description for email activities, show for others */}
                        {activity.description && activity.parentObject !== 'email_log' && (
                          <div className="activity-description activity-description--preview">
                            {getTruncatedDescriptionText(activity.description, activity.parentObject)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
            );
          })
        )}
      </div>

      {editingTaskId && (
        <EditLayoutModal
          show={!!editingTaskId}
          onHide={() => setEditingTaskId(null)}
          objectName="task"
          id={editingTaskId}
          sourcePage="detail"
          onSave={() => {
            setEditingTaskId(null);
            if (onRefresh) onRefresh();
          }}
          onCancel={() => setEditingTaskId(null)}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
};

export default TaskActivityTimeline;
